// The Record tab of a property (prototype §23a bmGlance + bmRecord): the record's completion, who to call,
// what is coming up, then the 32 sections by group as cards with a completion ring — a card opens the
// section in a drawer. One toolbar on top: find a section, and the Filter card's View (calendar / cards, remembered per
// browser) and Status (each with its count).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Phone } from 'lucide-react';
import { Card } from '../ui';
import { ListToolbar } from '../ui/ListToolbar';
import { FilterMenu, type FilterValue } from '../ui/FilterMenu';
import { StatTile } from '../charts/StatTile';
import { FormError } from '../forms/common';
import { buildingApi, DUE_COLOUR, STATUS_COLOUR, telHref, type SectionStatus } from '../../lib/building';
import { formatDate, formatMoney } from '../../lib/viz';
import { DueChip, Ring, StatusChip } from './bits';
import { SectionDrawer } from './SectionDrawer';
import { RecordCalendar } from './RecordCalendar';
import { formatPhone } from '../../lib/phone';

export function RecordTab({ propertyId, section, onSection, onTab, onContract, view, onView }: { propertyId: string; section: string | null; onSection: (id: string | null) => void; onTab: (tab: string) => void; /** Open one contract (else onTab('contracts')). */ onContract?: (id: string) => void; view: 'calendar' | 'cards'; onView: (v: 'calendar' | 'cards') => void }) {
  const openContract = (id: string) => (onContract ? onContract(id) : onTab('contracts'));
  // A date chosen on the calendar's +, handed to the section drawer when it opens.
  const [prefill, setPrefill] = useState<Record<string, string> | undefined>();
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const openSection = (id: string, pre?: Record<string, string>) => { setPrefill(pre); onSection(id); };
  const rec = useQuery({ queryKey: ['building-record', propertyId], queryFn: () => buildingApi.record(propertyId) });
  const schema = useQuery({ queryKey: ['building-schema'], queryFn: buildingApi.schema, staleTime: Infinity });
  const r = rec.data;
  if (rec.error) return <FormError error={rec.error} />;
  if (!r) return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-ink-100" />)}</div>;
  const c = r.glance.counts;
  const needle = search.trim().toLowerCase();
  const groups = r.groups.map((g) => ({ ...g, sections: g.sections.filter((x) => (!statuses.length || statuses.includes(x.effectiveStatus)) && (!needle || x.title.toLowerCase().includes(needle) || (x.description ?? '').toLowerCase().includes(needle))) })).filter((g) => g.sections.length);
  const filterValue: FilterValue = { view: [view], status: statuses };
  const applyFilters = (next: FilterValue) => { onView(next.view?.[0] === 'cards' ? 'cards' : 'calendar'); setStatuses(next.status ?? []); };
  return (
    <div className="space-y-2.5">
      <ListToolbar standalone search={view === 'cards' ? search : undefined} onSearch={view === 'cards' ? setSearch : undefined} searchPlaceholder="Find a section…"
        filters={<FilterMenu value={filterValue} onChange={applyFilters} groups={[
          { key: 'view', label: 'View', single: true, defaultValue: 'calendar', options: [{ value: 'calendar', label: 'Calendar' }, { value: 'cards', label: 'Cards' }] },
          ...(view === 'cards' ? [{ key: 'status', label: 'Status', options: (schema.data?.statuses ?? []).filter((x) => r.statusCounts[x.key]).map((x) => ({ value: x.key, label: `${x.label} (${r.statusCounts[x.key]})` })) }] : []),
        ]} />} />
      <div className="tile-row">
        <div className="surface flex items-center gap-3 p-4"><Ring pct={r.overall.pct} size={56} label="record" /><div className="min-w-0"><div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">Building record</div><div className="text-[12.5px] text-ink-600">{r.overall.filled} of {r.overall.total} filled</div></div></div>
        <StatTile label="Overdue" value={String(c.overdue)} hint={`${r.statusCounts.OVERDUE} section${r.statusCounts.OVERDUE === 1 ? '' : 's'} overdue`} />
        <StatTile label="Due in 90 days" value={String(c.dueIn90)} hint="renewals, reviews, notices" />
        <StatTile label="Contracts" value={c.contracts === null ? '—' : String(c.contracts)} hint={c.committedMinor !== null ? `${formatMoney(c.committedMinor, c.currency)} a year${c.section20 ? ` · ${c.section20} need s20` : ''}` : undefined} />
      </div>

      {view === 'calendar' && <RecordCalendar propertyId={propertyId} schema={schema.data} onSection={openSection} onContract={openContract} />}

      {view === 'cards' && <>
      <div className="grid gap-2.5 lg:grid-cols-3">
        <Card title="What is coming up" subtitle="The next 90 days and anything overdue" className="min-w-0 lg:col-span-2"
          actions={<button onClick={() => onView('calendar')} className="text-[12px] font-medium text-bronze-600 hover:underline">Open the calendar</button>}>
          {r.glance.keyDates.length ? (
            <ul className="-my-1 divide-y divide-ink-100">
              {r.glance.keyDates.map((d) => (
                <li key={d.id} className="flex items-center gap-3 py-2" style={{ boxShadow: `inset 3px 0 0 ${DUE_COLOUR[d.due.key]}` }}>
                  <span className="w-[74px] shrink-0 pl-3 text-[11.5px] tabular-nums text-ink-500">{formatDate(d.date)}</span>
                  <button className="min-w-0 flex-1 text-left" onClick={() => { if (d.ref.sectionId) openSection(d.ref.sectionId); else if (d.ref.contractId) openContract(d.ref.contractId); }}>
                    <span className="block truncate text-[13px] font-medium text-ink-800">{d.label}</span>
                    {d.sub && <span className="block truncate text-[11.5px] text-ink-400">{d.sub}</span>}
                  </button>
                  <DueChip due={d.due} />
                </li>
              ))}
            </ul>
          ) : <p className="py-4 text-center text-[12.5px] text-ink-400"><CalendarClock className="mx-auto mb-1 h-5 w-5 text-ink-300" />Nothing dated yet. Fill in renewal dates on the record, or add a supplier contract, and they appear here.</p>}
        </Card>
        <Card title="Who to call" className="min-w-0">
          {r.glance.whoToCall.length ? (
            <ul className="-my-1 divide-y divide-ink-100">
              {r.glance.whoToCall.map((w, i) => (
                <li key={`${w.role}-${i}`} className="py-2">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-400">{w.emergency && <AlertTriangle className="h-3 w-3 text-red-500" />}{w.role}</div>
                  <div className="text-[13px] text-ink-800">{w.name ?? '—'}</div>
                  {w.phone && <a href={telHref(w.phone)} className="inline-flex items-center gap-1 text-[12.5px] font-medium tabular-nums text-bronze-700 hover:underline"><Phone className="h-3 w-3" />{formatPhone(w.phone)}</a>}
                  {w.email && <a href={`mailto:${w.email}`} className="block truncate text-[12px] text-ink-500 hover:underline">{w.email}</a>}
                </li>
              ))}
            </ul>
          ) : <p className="py-4 text-center text-[12.5px] text-ink-400">Add the property manager and out-of-hours numbers on Development Overview and they appear here.</p>}
        </Card>
      </div>

      {(needle || statuses.length) && !groups.length && <p className="surface px-4 py-8 text-center text-[12.5px] text-ink-500">No section matches.</p>}
      {groups.map((g) => (
        <section key={g.group}>
          <div className="mb-2 flex items-center gap-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">{g.group}</h3>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100"><div className="h-full rounded-full bg-bronze-400" style={{ width: `${g.pct}%` }} /></div>
            <span className="text-[11.5px] tabular-nums text-ink-500">{g.pct}%</span>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {g.sections.map((s) => (
              <button key={s.id} onClick={() => openSection(s.id)} className="surface flex items-start gap-3 p-3.5 text-left transition hover:-translate-y-[1px] hover:shadow-md"
                style={{ boxShadow: `inset 3px 0 0 ${STATUS_COLOUR[s.effectiveStatus]}` }}>
                <Ring pct={s.progress.pct} size={42} colour={STATUS_COLOUR[s.effectiveStatus as SectionStatus] === '#a3a8b0' ? 'var(--color-bronze-500)' : STATUS_COLOUR[s.effectiveStatus]} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-ink-900"><span className="mr-1 text-ink-300">{s.n}</span>{s.title}</span>
                  <span className="mt-0.5 line-clamp-2 block text-[11.5px] leading-snug text-ink-500">{s.description}</span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <StatusChip status={s.effectiveStatus} label={s.effectiveStatusLabel} />
                    <span className="text-[11px] text-ink-400">{s.progress.filled}/{s.progress.total}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
      </>}

      {section && schema.data && (
        <SectionDrawer propertyId={propertyId} sectionId={section} statuses={schema.data.statuses} prefill={prefill} onClose={() => { onSection(null); setPrefill(undefined); }} onSaved={() => undefined} />
      )}
    </div>
  );
}
